/**
 * UPT-2124 J4a (spec `web/SPEC-UPT-2124-J4.md` §2 n° 2, 10) — `ui_read` : le RÉSUMÉ TEXTUEL de ce qui est à l'écran (titre, onglets, champs visibles
 * avec leur valeur, boutons, lignes de liste ≤ 20), BORNÉ à 2 000 caractères. Rien d'autre n'est envoyé au modèle : pas de capture, pas de DOM.
 * Les données masquées sont EXCLUES (champ `password`, `data-sensitive`, valeur `••••`, hors périmètre) et aucun jeton n'y passe (`scrubSecrets`).
 * Décision d'Olivier du 20.10 (vie privée) : les valeurs des champs PROTÉGÉS — classés avec clef (C3 et plus, compartiments comme « données
 * personnelles ») ou tokenisés, repérés par la clef `[data-classification-key]` (`ClassificationKey`) sur leur libellé ou leur en-tête de
 * colonne — ne partent JAMAIS au modèle : `[protected data]` à la place. Une carte ou un lien qui contient une telle valeur est nommé par sa
 * position (« item 2 ») ; l'utilisateur peut toujours le désigner ainsi, la résolution par nom se fait dans le navigateur.
 * Les boutons portent leur niveau : `[confirm]` (carte de confirmation) ou `[screen only]` (interdit à la voix), pour que le modèle sache d'avance.
 *
 * Pur : lit le document, n'écrit rien.
 */
import {
  accessibleName,
  collectCandidates,
  inDestructiveModal,
  isDisabled,
  isSensitive,
  looksMasked,
  readFieldValue,
  resolveCandidate,
  REGION_ROLES,
  scopeRoots,
  submitsForm,
  type UiCandidate,
} from './uiActor';
import { classifyUiGesture } from './uiRiskTable';

/** Borne du résumé envoyé au modèle. */
export const UI_READ_MAX_CHARS = 2000;
export const UI_READ_MAX_ROWS = 20;
const MAX_FIELDS = 25;
const MAX_BUTTONS = 25;
const MAX_LINKS = 10;

const TOKEN_LIKE = /\b(?:eyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]*|[A-Za-z0-9+/_-]{40,}={0,2})/g;
const MASK_RUN = /[•●∗]+|\*{4,}/g;

/** Retire d'un texte ce qui ressemble à un jeton (JWT, longue suite opaque) et les suites masquées. */
export const scrubSecrets = (text: string): string => text.replace(TOKEN_LIKE, '…').replace(MASK_RUN, '');

const clip = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

const oneLine = (text: string): string => scrubSecrets(text).replace(/\s+/g, ' ').trim();

export type UiReadResult =
  | { ok: true; text: string }
  | { ok: false; error: 'region_not_found'; candidates: string[] }
  | { ok: false; error: 'region_ambiguous'; candidates: string[] };

/** Valeur affichée à la place d'une donnée protégée (jamais la valeur elle-même). */
export const PROTECTED_VALUE = '[protected data]';

const KEY_SELECTOR = '[data-classification-key]';
const CONTROL_SELECTOR = 'input, select, textarea, [role="combobox"], [role="textbox"]';

/** Le champ est-il protégé ? Clef sur l'un de ses libellés, ou dans le plus proche conteneur qui n'enveloppe que ce contrôle (4 niveaux au plus). */
export const isProtectedField = (element: HTMLElement): boolean => {
  const labels = (element as HTMLInputElement).labels;
  if (labels && Array.from(labels).some((label) => label.querySelector(KEY_SELECTOR) !== null)) return true;
  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy) {
    const doc = element.ownerDocument;
    if (labelledBy.split(/\s+/).some((id) => doc.getElementById(id)?.querySelector(KEY_SELECTOR) != null)) return true;
  }
  let node: HTMLElement | null = element.parentElement;
  for (let depth = 0; node !== null && depth < 4; depth += 1, node = node.parentElement) {
    if (node.querySelectorAll(CONTROL_SELECTOR).length > 1) return false;
    if (node.querySelector(KEY_SELECTOR) !== null) return true;
  }
  return false;
};

const CELL_SELECTOR = ':scope > td, :scope > th, :scope > [role="gridcell"], :scope > [role="cell"], :scope > [role="rowheader"]';
const HEADER_CELL_SELECTOR = ':scope > th, :scope > [role="columnheader"]';

/** Texte d'une ligne de tableau, les cellules des colonnes protégées (clef sur l'en-tête) remplacées ; null si la ligne n'a pas de cellules lisibles. */
export const rowTextWithoutProtected = (row: HTMLElement): string | null => {
  const cells = Array.from(row.querySelectorAll<HTMLElement>(CELL_SELECTOR));
  if (cells.length === 0) return null;
  const table = row.closest('table, [role="grid"], [role="table"], [role="treegrid"]');
  const headerRow = table ? Array.from(table.querySelectorAll<HTMLElement>('tr, [role="row"]')).find((candidate) => candidate.querySelector(HEADER_CELL_SELECTOR) !== null && candidate.querySelector('td, [role="gridcell"]') === null) : undefined;
  const headers = headerRow ? Array.from(headerRow.querySelectorAll<HTMLElement>(HEADER_CELL_SELECTOR)) : [];
  return cells
    .map((cell, index) => (cell.querySelector(KEY_SELECTOR) !== null || headers[index]?.querySelector(KEY_SELECTOR) != null ? PROTECTED_VALUE : (cell.textContent ?? '').trim()))
    .filter((text) => text !== '')
    .join(' · ');
};

/** Un élément (carte, lien, ligne sans cellules) qui contient une donnée protégée. */
const containsProtected = (element: HTMLElement): boolean => element.querySelector(KEY_SELECTOR) !== null;

const FIELD_ROLES = new Set(['textbox', 'spinbutton', 'combobox', 'checkbox', 'switch', 'radio', 'listbox']);

const fieldLine = (candidate: UiCandidate): string | null => {
  const { element, name } = candidate;
  if (isSensitive(element) || looksMasked(name)) return null;
  const value = readFieldValue(element);
  const shown = isProtectedField(element) ? PROTECTED_VALUE : typeof value === 'boolean' ? (value ? '[x]' : '[ ]') : oneLine(value);
  const required = element.getAttribute('aria-required') === 'true' || (element as HTMLInputElement).required === true;
  return `${clip(oneLine(name), 50)}${required ? '*' : ''} = ${shown ? clip(shown, 60) : '(empty)'}${(element as HTMLInputElement).readOnly ? ' (read-only)' : ''}${isDisabled(element) ? ' (disabled)' : ''}`;
};

const buttonLine = (candidate: UiCandidate): string | null => {
  const { element, name, role } = candidate;
  if (looksMasked(name) || isSensitive(element)) return null;
  const decision = classifyUiGesture(name, {
    dataVoice: element.closest('[data-voice]')?.getAttribute('data-voice') ?? null,
    inDestructiveModal: inDestructiveModal(element),
    submits: submitsForm(element),
    role,
  });
  const flags = [decision.risk === 'confirm' ? 'confirm' : decision.risk === 'forbidden' ? 'screen only' : '', isDisabled(element) ? 'disabled' : ''].filter(Boolean);
  return `${clip(oneLine(name), 50)}${flags.length > 0 ? ` [${flags.join(', ')}]` : ''}`;
};

const isHeaderRow = (element: HTMLElement): boolean => element.closest('thead') !== null || (element.querySelector('td, [role="gridcell"]') === null && element.querySelector('th, [role="columnheader"]') !== null);

/** Résume l'écran ; `region` (le nom d'un dialogue, d'un onglet-panneau, d'un formulaire, d'un tableau…) restreint la lecture à cette zone. */
export const readScreen = (doc: Document, region?: string): UiReadResult => {
  let roots = scopeRoots(doc);
  if (region && region.trim()) {
    const regions = collectCandidates(doc, roots).filter((candidate) => REGION_ROLES.includes(candidate.role));
    const resolution = resolveCandidate(regions, region);
    if (resolution.kind === 'none') {
      return { ok: false, error: 'region_not_found', candidates: regions.slice(0, 8).map((candidate) => candidate.name) };
    }
    if (resolution.kind === 'ambiguous') {
      return { ok: false, error: 'region_ambiguous', candidates: resolution.candidates.slice(0, 8).map((candidate) => candidate.name) };
    }
    roots = [resolution.candidate.element];
  }
  const candidates = collectCandidates(doc, roots);
  const lines: string[] = [];

  // Le titre de la page : le h1, sinon le premier titre qui n'est pas celui d'une carte ou d'une ligne de liste.
  const heading =
    candidates.find((candidate) => candidate.role === 'heading' && candidate.element.tagName === 'H1') ??
    candidates.find((candidate) => candidate.role === 'heading' && candidate.element.closest('li, article, tr, [role="row"], [role="listitem"], [role="dialog"]') === null);
  // Sans titre dans le contenu, le titre de page de l'en-tête (lu seulement : l'en-tête reste hors du périmètre des gestes), puis le titre du document.
  const headerTitle = doc.querySelector('[data-voice-title]')?.textContent ?? '';
  const title = oneLine(heading?.name ?? (headerTitle.trim() || doc.title || ''));
  if (title) lines.push(`Page: ${clip(title, 100)}`);

  candidates
    .filter((candidate) => candidate.role === 'dialog')
    .slice(0, 2)
    .forEach((candidate) => lines.push(`Dialog: ${clip(oneLine(candidate.name), 80)}`));

  const tabs = candidates.filter((candidate) => candidate.role === 'tab');
  if (tabs.length > 0) {
    lines.push(`Tabs: ${tabs.slice(0, 12).map((tab) => `${clip(oneLine(tab.name), 30)}${tab.element.getAttribute('aria-selected') === 'true' ? '*' : ''}`).join(', ')}`);
  }

  const fields = candidates.filter((candidate) => FIELD_ROLES.has(candidate.role)).map(fieldLine).filter((line): line is string => line !== null);
  if (fields.length > 0) {
    lines.push('Fields:');
    fields.slice(0, MAX_FIELDS).forEach((line) => lines.push(`- ${line}`));
  }

  const buttons = candidates.filter((candidate) => candidate.role === 'button' || candidate.role === 'menuitem').map(buttonLine).filter((line): line is string => line !== null);
  if (buttons.length > 0) {
    lines.push(`Buttons: ${buttons.slice(0, MAX_BUTTONS).join(', ')}`);
  }

  const rows = candidates.filter((candidate) => candidate.role === 'row' && !isHeaderRow(candidate.element) && !looksMasked(candidate.name));
  if (rows.length > 0) {
    lines.push(`Rows (${Math.min(rows.length, UI_READ_MAX_ROWS)} of ${rows.length}):`);
    rows.slice(0, UI_READ_MAX_ROWS).forEach((row, index) => {
      const safe = rowTextWithoutProtected(row.element) ?? (containsProtected(row.element) ? `item ${index + 1} (${PROTECTED_VALUE})` : row.name);
      lines.push(`- ${clip(oneLine(safe), 100)}`);
    });
  }

  const links = candidates.filter((candidate) => candidate.role === 'link' && !looksMasked(candidate.name));
  if (links.length > 0) {
    lines.push(`Links: ${links.slice(0, MAX_LINKS).map((link, index) => (containsProtected(link.element) ? `item ${index + 1} (${PROTECTED_VALUE})` : clip(oneLine(link.name), 40))).join(', ')}`);
  }

  let text = '';
  for (const line of lines) {
    const next = text ? `${text}\n${line}` : line;
    if (next.length > UI_READ_MAX_CHARS - 1) {
      text = `${text}\n…`.slice(0, UI_READ_MAX_CHARS);
      break;
    }
    text = next;
  }
  return { ok: true, text: text || '(empty)' };
};

/** Le nom lisible d'une zone, pour les messages (le nom accessible de l'élément). */
export const regionName = (element: Element): string => accessibleName(element);
